'use client'

import { useAuth } from '@/hooks/use-auth'
import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  activeCrmIntegrationsQueryKey,
  crmPipelinesQueryKey,
  type ActiveCrmIntegrationView,
} from '@/hooks/queries/crm-integrations'
import { getDeliveryQueryTenantId } from '@/hooks/queries/delivery-integrations'
import type { CrmProviderType, CrmCapabilities, CrmTerminology } from '@/lib/crm/types'
import type { CrmAuthMethodInfo } from '@/lib/crm/adapters/registry'

// =====================================================
// Types
// =====================================================

export interface CrmIntegrationApiError {
  error: string
  errorType?: string
  troubleshooting?: string[]
  details?: string
}

export interface CrmIntegrationRecord {
  id: string
  provider: CrmProviderType
  name: string
  isActive: boolean
  metadata: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

export interface CrmProviderInfo {
  id: CrmProviderType
  name: string
  description: string
  logoUrl: string
  color: string
  implemented: boolean
  authMethods: CrmAuthMethodInfo[]
  terminology: CrmTerminology
  capabilities: CrmCapabilities
  /**
   * Provider has a chat adapter AND this environment has its platform channel
   * credentials. Server-answered: the credentials are not visible to the client.
   */
  chatAvailable: boolean
}

interface UseCrmIntegrationsOptions {
  autoLoad?: boolean
}

const DEFAULT_OPTIONS: Required<UseCrmIntegrationsOptions> = {
  autoLoad: true,
}

const asApiError = (data: unknown, fallback: string): CrmIntegrationApiError => {
  const d = data as Record<string, unknown> | null
  return {
    error: typeof d?.error === 'string' ? d.error : fallback,
    errorType: typeof d?.errorType === 'string' ? d.errorType : undefined,
    troubleshooting: Array.isArray(d?.troubleshooting) ? d!.troubleshooting as string[] : undefined,
    details: typeof d?.details === 'string' ? d.details : undefined,
  }
}

// =====================================================
// Hook
// =====================================================

export function useCrmIntegrations(options?: UseCrmIntegrationsOptions) {
  const { user, fetchWithAuth } = useAuth()
  const queryClient = useQueryClient()
  const tenantId = getDeliveryQueryTenantId(user)
  const { autoLoad } = { ...DEFAULT_OPTIONS, ...options }

  const [integrations, setIntegrations] = useState<CrmIntegrationRecord[]>([])
  const [providers, setProviders] = useState<CrmProviderInfo[]>([])
  const [error, setError] = useState<CrmIntegrationApiError | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingProviders, setIsLoadingProviders] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [testingId, setTestingId] = useState<string | null>(null)
  const [updatingCredentialsId, setUpdatingCredentialsId] = useState<string | null>(null)
  const [reconnectingId, setReconnectingId] = useState<string | null>(null)

  const refreshCrmMetadataQueries = useCallback(
    (integration: CrmIntegrationRecord) => {
      queryClient.setQueryData<ActiveCrmIntegrationView[]>(
        activeCrmIntegrationsQueryKey(tenantId),
        current => updateActiveCrmIntegrationCache(current, integration),
      )
      void Promise.all([
        queryClient.invalidateQueries({
          queryKey: activeCrmIntegrationsQueryKey(tenantId),
        }),
        queryClient.invalidateQueries({
          queryKey: crmPipelinesQueryKey(tenantId, integration.id),
        }),
      ]).catch(error => {
        console.error('[useCrmIntegrations] CRM metadata refresh failed', error)
      })
    },
    [queryClient, tenantId],
  )

  const removeCrmMetadataQueries = useCallback(
    (integrationId: string) => {
      queryClient.setQueryData<ActiveCrmIntegrationView[]>(
        activeCrmIntegrationsQueryKey(tenantId),
        current => removeActiveCrmIntegration(current, integrationId),
      )
      queryClient.removeQueries({
        queryKey: crmPipelinesQueryKey(tenantId, integrationId),
      })
      void queryClient.invalidateQueries({
        queryKey: activeCrmIntegrationsQueryKey(tenantId),
      }).catch(error => {
        console.error('[useCrmIntegrations] active CRM refresh failed', error)
      })
    },
    [queryClient, tenantId],
  )

  // Fetch all integrations for tenant
  const refreshIntegrations = useCallback(async () => {
    if (!user) {
      setIntegrations([])
      return
    }

    setIsLoading(true)
    try {
      const response = await fetchWithAuth('/api/crm/integrations', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        setError(asApiError(data, 'Failed to fetch CRM integrations'))
        setIntegrations([])
        return
      }

      const data = await response.json()
      setIntegrations(Array.isArray(data?.integrations) ? data.integrations : [])
    } catch (err) {
      console.error('[useCrmIntegrations] refreshIntegrations failed', err)
      setError({
        error: 'Network error while loading CRM integrations',
        errorType: 'network',
        troubleshooting: [
          'Check your internet connection',
          'Try again in a few minutes',
          'Contact support if the issue persists',
        ],
      })
      setIntegrations([])
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth, user])

  // Fetch all available providers
  const refreshProviders = useCallback(async () => {
    if (!user) {
      setProviders([])
      return
    }

    setIsLoadingProviders(true)
    try {
      const response = await fetchWithAuth('/api/crm/providers', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })

      if (!response.ok) {
        console.error('[useCrmIntegrations] Failed to fetch providers')
        setProviders([])
        return
      }

      const data = await response.json()
      setProviders(Array.isArray(data?.providers) ? data.providers : [])
    } catch (err) {
      console.error('[useCrmIntegrations] refreshProviders failed', err)
      setProviders([])
    } finally {
      setIsLoadingProviders(false)
    }
  }, [fetchWithAuth, user])

  // Initial load
  useEffect(() => {
    if (!autoLoad) return
    void refreshIntegrations()
    void refreshProviders()
  }, [autoLoad, refreshIntegrations, refreshProviders])

  // Connect a new integration
  const connectIntegration = useCallback(
    async (provider: CrmProviderType, name: string, credentials: Record<string, unknown>, metadata?: Record<string, unknown>) => {
      if (!user) return { success: false, error: 'Not authenticated' }

      setIsConnecting(true)
      setError(null)

      try {
        const response = await fetchWithAuth('/api/crm/integrations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ provider, name, credentials, metadata }),
        })

        const data = await response.json().catch(() => ({}))

        if (!response.ok) {
          const apiError = asApiError(data, 'Failed to connect CRM')
          setError(apiError)
          return { success: false, error: apiError.error }
        }

        const integration = data.integration as CrmIntegrationRecord
        refreshCrmMetadataQueries(integration)
        await refreshIntegrations()
        return { success: true, integration: data.integration }
      } catch (err) {
        console.error('[useCrmIntegrations] connectIntegration failed', err)
        const apiError: CrmIntegrationApiError = {
          error: 'Network error while connecting CRM',
          errorType: 'network',
          troubleshooting: [
            'Check your internet connection',
            'Verify CRM is reachable',
            'Try again in a few minutes',
          ],
        }
        setError(apiError)
        return { success: false, error: apiError.error }
      } finally {
        setIsConnecting(false)
      }
    },
    [fetchWithAuth, refreshCrmMetadataQueries, refreshIntegrations, user],
  )

  // Prepare an OAuth-mode integration and return the provider's authorize URL for the caller to
  // redirect to. Two authenticated fetches (both need the JWT bearer, which a top-level browser
  // navigation can't carry): (1) pre-create a pending (inactive) row via prepare-oauth, then
  // (2) hit /api/oauth/<provider>/start which sets the encrypted state cookie and returns the
  // authorize URL as JSON. Real credentials land on the OAuth callback, which activates the row.
  const prepareOAuthInstall = useCallback(
    async (provider: CrmProviderType, subdomain: string, name: string) => {
      if (!user) return { success: false, error: 'Not authenticated' }
      setError(null)

      try {
        const prepareResp = await fetchWithAuth(`/api/crm/integrations/${provider}/prepare-oauth`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ subdomain, name }),
        })

        const prepareData = await prepareResp.json().catch(() => ({}))

        if (!prepareResp.ok) {
          const apiError = asApiError(prepareData, 'Failed to start CRM install')
          setError(apiError)
          return { success: false, error: apiError.error }
        }

        const integrationId = prepareData.integrationId as string
        const returnTo =
          typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/'
        const startResp = await fetchWithAuth(
          `/api/oauth/${provider}/start` +
            `?integration_id=${encodeURIComponent(integrationId)}` +
            `&return_to=${encodeURIComponent(returnTo)}`,
          { method: 'GET', headers: { Accept: 'application/json' } },
        )

        const startData = await startResp.json().catch(() => ({}))

        if (!startResp.ok || !startData.authorizeUrl) {
          const apiError = asApiError(startData, 'Failed to start CRM authorization')
          setError(apiError)
          return { success: false, integrationId, error: apiError.error }
        }

        return {
          success: true,
          integrationId,
          authorizeUrl: startData.authorizeUrl as string,
        }
      } catch (err) {
        console.error('[useCrmIntegrations] prepareOAuthInstall failed', err)
        const apiError: CrmIntegrationApiError = {
          error: 'Network error while starting CRM install',
          errorType: 'network',
        }
        setError(apiError)
        return { success: false, error: apiError.error }
      }
    },
    [fetchWithAuth, user],
  )

  // Reconnect an existing OAuth integration: re-run the OAuth handshake against the SAME row.
  // Unlike `prepareOAuthInstall`, this skips `prepare-oauth` entirely — the row already exists with
  // a subdomain, and `/start` never touches credentials, so abandoning the consent screen leaves the
  // current (possibly still-valid) tokens intact. The callback updates the same row in place, so
  // bindings survive and no duplicate integration is ever created. The caller navigates to the
  // returned `authorizeUrl`.
  const reconnectOAuth = useCallback(
    async (provider: CrmProviderType, integrationId: string) => {
      if (!user) return { success: false, error: 'Not authenticated' }
      setError(null)
      setReconnectingId(integrationId)

      try {
        const returnTo =
          typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/'
        const startResp = await fetchWithAuth(
          `/api/oauth/${provider}/start` +
            `?integration_id=${encodeURIComponent(integrationId)}` +
            `&return_to=${encodeURIComponent(returnTo)}`,
          { method: 'GET', headers: { Accept: 'application/json' } },
        )

        const startData = await startResp.json().catch(() => ({}))

        if (!startResp.ok || !startData.authorizeUrl) {
          const apiError = asApiError(startData, 'Failed to start CRM reconnect')
          setError(apiError)
          return { success: false, error: apiError.error }
        }

        return { success: true, authorizeUrl: startData.authorizeUrl as string }
      } catch (err) {
        console.error('[useCrmIntegrations] reconnectOAuth failed', err)
        const apiError: CrmIntegrationApiError = {
          error: 'Network error while starting CRM reconnect',
          errorType: 'network',
        }
        setError(apiError)
        return { success: false, error: apiError.error }
      } finally {
        setReconnectingId(null)
      }
    },
    [fetchWithAuth, user],
  )

  // Disconnect (delete) an integration
  const disconnectIntegration = useCallback(
    async (integrationId: string) => {
      if (!user) return false
      setError(null)
      setDisconnectingId(integrationId)

      try {
        const response = await fetchWithAuth(`/api/crm/integrations/${integrationId}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        })

        const data = await response.json().catch(() => ({}))

        if (!response.ok) {
          setError(asApiError(data, 'Failed to disconnect CRM'))
          return false
        }

        setIntegrations((prev) => prev.filter((i) => i.id !== integrationId))
        removeCrmMetadataQueries(integrationId)
        return true
      } catch (err) {
        console.error('[useCrmIntegrations] disconnectIntegration failed', err)
        setError({
          error: 'Network error while disconnecting CRM',
          errorType: 'network',
        })
        return false
      } finally {
        setDisconnectingId(null)
      }
    },
    [fetchWithAuth, removeCrmMetadataQueries, user],
  )

  // Toggle integration active state
  const toggleIntegration = useCallback(
    async (integrationId: string, isActive: boolean) => {
      if (!user) return false
      setError(null)
      setTogglingId(integrationId)

      try {
        const response = await fetchWithAuth(`/api/crm/integrations/${integrationId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ isActive }),
        })

        const data = await response.json().catch(() => ({}))

        if (!response.ok) {
          setError(asApiError(data, 'Failed to update CRM integration'))
          return false
        }

        const integration = data.integration as CrmIntegrationRecord
        setIntegrations((prev) => prev.map((item) => (
          item.id === integrationId ? integration : item
        )))
        refreshCrmMetadataQueries(integration)
        return true
      } catch (err) {
        console.error('[useCrmIntegrations] toggleIntegration failed', err)
        setError({
          error: 'Network error while updating integration',
          errorType: 'network',
        })
        return false
      } finally {
        setTogglingId(null)
      }
    },
    [fetchWithAuth, refreshCrmMetadataQueries, user],
  )

  // Test integration connection
  const testIntegration = useCallback(
    async (integrationId: string) => {
      if (!user) return { success: false, error: 'Not authenticated' }
      setError(null)
      setTestingId(integrationId)

      try {
        const response = await fetchWithAuth(`/api/crm/integrations/${integrationId}/test`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        })

        const data = await response.json().catch(() => ({}))

        if (!response.ok) {
          const apiError = asApiError(data, 'Connection test failed')
          setError(apiError)
          return { success: false, error: apiError.error }
        }

        return { success: true, message: data.message }
      } catch (err) {
        console.error('[useCrmIntegrations] testIntegration failed', err)
        const apiError: CrmIntegrationApiError = {
          error: 'Network error during connection test',
          errorType: 'network',
        }
        setError(apiError)
        return { success: false, error: apiError.error }
      } finally {
        setTestingId(null)
      }
    },
    [fetchWithAuth, user],
  )

  // Update credentials for an existing integration
  const updateCredentials = useCallback(
    async (integrationId: string, credentials: Record<string, unknown>, metadata?: Record<string, unknown>) => {
      if (!user) return { success: false, error: 'Not authenticated' }

      setUpdatingCredentialsId(integrationId)
      setError(null)

      try {
        const body: Record<string, unknown> = { credentials }
        if (metadata) body.metadata = metadata

        const response = await fetchWithAuth(`/api/crm/integrations/${integrationId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        })

        const data = await response.json().catch(() => ({}))

        if (!response.ok) {
          const apiError = asApiError(data, 'Failed to update credentials')
          setError(apiError)
          return { success: false, error: apiError.error }
        }

        refreshCrmMetadataQueries(data.integration as CrmIntegrationRecord)
        await refreshIntegrations()
        return { success: true }
      } catch (err) {
        console.error('[useCrmIntegrations] updateCredentials failed', err)
        const apiError: CrmIntegrationApiError = {
          error: 'Network error while updating credentials',
          errorType: 'network',
        }
        setError(apiError)
        return { success: false, error: apiError.error }
      } finally {
        setUpdatingCredentialsId(null)
      }
    },
    [fetchWithAuth, refreshCrmMetadataQueries, refreshIntegrations, user],
  )

  // Get provider by ID
  const getProvider = useCallback(
    (providerId: CrmProviderType): CrmProviderInfo | undefined => {
      return providers.find((p) => p.id === providerId)
    },
    [providers],
  )

  // Kommo defaults to the OAuth flow. The "API key" (long_token) method is hidden from regular
  // users — super users always see it, and regular users can opt in via the `?kommoApiKey=1` URL
  // param (kept undocumented, for support / manual setups).
  const searchParams = useSearchParams()
  const showKommoApiKey = Boolean(user?.isSuperUser) || searchParams.get('kommoApiKey') === '1'
  const visibleProviders = useMemo(() => {
    if (showKommoApiKey) return providers
    return providers.map((p) =>
      p.id === 'kommo'
        ? { ...p, authMethods: p.authMethods.filter((m) => m.method !== 'long_token') }
        : p,
    )
  }, [providers, showKommoApiKey])

  // Get only implemented providers
  const implementedProviders = useMemo(
    () => visibleProviders.filter((p) => p.implemented),
    [visibleProviders],
  )

  // Stats
  const stats = useMemo(() => {
    const total = integrations.length
    const active = integrations.filter((i) => i.isActive).length
    return { total, active }
  }, [integrations])

  return {
    // Data
    integrations,
    providers: visibleProviders,
    implementedProviders,
    stats,
    error,

    // Loading states
    isLoading,
    isLoadingProviders,
    isConnecting,
    disconnectingId,
    togglingId,
    testingId,
    updatingCredentialsId,
    reconnectingId,

    // Actions
    connectIntegration,
    prepareOAuthInstall,
    reconnectOAuth,
    disconnectIntegration,
    toggleIntegration,
    testIntegration,
    updateCredentials,
    refreshIntegrations,
    refreshProviders,
    getProvider,
    clearError: () => setError(null),
  }
}

function updateActiveCrmIntegrationCache(
  current: ActiveCrmIntegrationView[] | undefined,
  integration: CrmIntegrationRecord,
): ActiveCrmIntegrationView[] {
  const withoutIntegration = removeActiveCrmIntegration(current, integration.id)
  if (!integration.isActive) return withoutIntegration
  return [
    ...withoutIntegration,
    {
      id: integration.id,
      name: integration.name,
      isActive: true,
    },
  ]
}

function removeActiveCrmIntegration(
  current: ActiveCrmIntegrationView[] | undefined,
  integrationId: string,
): ActiveCrmIntegrationView[] {
  return (current ?? []).filter(integration => integration.id !== integrationId)
}
